/**
 * @file Frontend utilities — a2TransportCarbonCalculations.ts.
 */

import type { FormSubSection } from "../interfaces/formInterfaces";
import type { VehicleCategoryEmissionFactorPublic } from "../services/vehicleCategoryEmissionFactorsPublicService";
import { normalizeUnitToken } from "../pages/Dashboard/Contributor/Stages/shared/fuelMixReferenceAutofill";
import { resolveUnitSelectValueFromLabel } from "../pages/Dashboard/Contributor/Stages/shared/unitSelectOptions";
import { formatEmbodiedCarbonReferenceQty, formatEmbodiedCarbonFunctionalUnit } from "../pages/Dashboard/Contributor/Stages/shared/embodiedCarbonReferenceQtyFormat";

function sumPerMaterialCounts(counts: number[], endExclusive: number) {
  let s = 0;
  for (let i = 0; i < endExclusive; i++) {
    s += Math.max(0, counts[i] ?? 0);
  }
  return s;
}

/** Exported function — parse Leading Number From Display. */
export function parseLeadingNumberFromDisplay(s: string): number | null {
  const t = String(s ?? "").trim();
  const m = t.match(/^[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
}

/**
 * Match stored `vehicleCategory` (prefer String(row.id)) to a public emission-factor row.
 * Falls back to label match for legacy payloads that saved the label text.
 */
export function findVehicleCategoryEmissionFactor(
  vehicleCategoryValue: string | undefined,
  rows: VehicleCategoryEmissionFactorPublic[]
): VehicleCategoryEmissionFactorPublic | undefined {
  const sel = String(vehicleCategoryValue ?? "").trim();
  if (!sel || !rows.length) return undefined;

  const byId = rows.find((r) => String(r.id) === sel);
  if (byId) return byId;

  const lower = sel.toLowerCase();
  return rows.find((r) => {
    const lbl = (r.categoryLabel || "").toLowerCase().trim();
    return (
      lbl === lower ||
      (lbl.length > 0 && (lbl.includes(lower) || lower.includes(lbl)))
    );
  });
}

/**
 * True when factor is expressed per tonne·km (background raw material qty assumed kg → convert to tonnes).
 */
export function isPerTonneKmCharacterizationUnit(unitStr: string): boolean {
  const u = String(unitStr ?? "").toLowerCase();
  return (
    /\btonne\b/.test(u) ||
    /\bton\s*\*\s*km\b/.test(u) ||
    /t\s*\*\s*km/.test(u) ||
    /\bt\/km/.test(u) ||
    (/ton/.test(u) && /km/.test(u))
  );
}

function resolveUnitValueForCarbonField(
  carbon: FormSubSection,
  unitFieldName: string,
  desiredUnitToken: string,
  unitIdByToken: Record<string, number>
): string {
  const unitField = carbon.fields?.find((f: { name?: string }) => f?.name === unitFieldName);
  const opts = unitField?.dropdown?.options || unitField?.dropdownValues || [];
  return resolveUnitSelectValueFromLabel(
    desiredUnitToken,
    unitIdByToken,
    Array.isArray(opts) ? opts : []
  );
}

function parseVehicleCapacityTonnes(raw: string | undefined): number | null {
  const n = Number(String(raw ?? "").replace(/,/g, "").trim());
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function parseDistanceKm(raw: string | undefined): number | null {
  const n = Number(String(raw ?? "").replace(/,/g, "").trim());
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

const A2_VEHICLE_CODE = "A2_VEHICLE_DETAILS";
const A2_TRANSPORT_CODE = "A2_TRANSPORT_DETAILS";

/** Exported function — build A2Transport Characterization Updates. */
export function buildA2TransportCharacterizationUpdates(
  currentValues: Record<string, string>,
  formData: { formSubSections?: FormSubSection[] | undefined } | null,
  rawMaterialCount: number,
  vehiclesPerRawMaterial: number[],
  carbonCode: string,
  emissionFactorRows: VehicleCategoryEmissionFactorPublic[],
  cfManualByMaterialRow: Record<string, boolean>,
  unitIdByToken: Record<string, number>
): {
  updates: Record<string, string>;
  cfUnitByMaterial: Record<number, string>;
} {
  const updates: Record<string, string> = {};
  const cfUnitByMaterial: Record<number, string> = {};

  if (
    !formData?.formSubSections?.length ||
    !emissionFactorRows.length ||
    rawMaterialCount <= 0
  ) {
    return { updates, cfUnitByMaterial };
  }

  const carbon = formData.formSubSections.find((s) => s.code === carbonCode);
  if (!carbon?.fields) return { updates, cfUnitByMaterial };

  const R = rawMaterialCount;
  const vCounts =
    vehiclesPerRawMaterial.length >= R
      ? vehiclesPerRawMaterial.map((c) => Math.max(1, c))
      : Array.from({ length: R }, () => 1);

  for (let m = 0; m < R; m++) {
    const rowKey = `row${m}`;
    if (cfManualByMaterialRow[rowKey]) continue;

    const nVehicles = Math.max(1, vCounts[m] ?? 1);
    if (nVehicles > 1) {
      continue;
    }

    const vStart = sumPerMaterialCounts(vCounts, m);
    const cat =
      currentValues[`vehicleCategory_${A2_VEHICLE_CODE}_row${vStart}`] ?? "";
    const pub = findVehicleCategoryEmissionFactor(cat, emissionFactorRows);
    if (!pub) continue;

    const numeric = parseLeadingNumberFromDisplay(pub.emissionFactor);
    if (numeric == null || !Number.isFinite(numeric)) continue;

    const cfKey = `characterizationFactor_${carbonCode}_row${m}`;
    const unitValueKey = `characterizationFactor_unit_${carbonCode}_row${m}`;

    updates[cfKey] = String(numeric);
    cfUnitByMaterial[m] = String(pub.unit ?? "").trim();

    const resolved = resolveUnitValueForCarbonField(
      carbon,
      "characterizationFactor_unit",
      String(pub.unit ?? "").trim(),
      unitIdByToken
    );
    if (resolved && currentValues[unitValueKey] !== resolved) {
      updates[unitValueKey] = resolved;
    }
  }

  return { updates, cfUnitByMaterial };
}

/**
 * Embodied carbon (kg CO₂e) for transport of one raw material.
 * kg CO₂eq = characterization factor × vehicle capacity (tonne) × total distance travelled (km).
 */
export function buildA2EmbodiedCarbonQuantityUpdates(
  currentValues: Record<string, string>,
  rawMaterials: { qty?: string }[],
  carbonCode: string,
  cfUnitHintByMaterial: Record<number, string>,
  outputManualByMaterialRow: Record<string, boolean>,
  vehiclesPerRawMaterial: number[],
  vehicleCategoryEmissionFactors: VehicleCategoryEmissionFactorPublic[],
  kgCo2eUnitId = "",
  functionalCarbonUnitValue = ""
): Record<string, string> {
  const fnUnitToken =
    String(functionalCarbonUnitValue ?? "").trim() || kgCo2eUnitId;
  const updates: Record<string, string> = {};
  const R = rawMaterials.length;

  const vCounts =
    vehiclesPerRawMaterial.length >= R
      ? vehiclesPerRawMaterial.map((c) => Math.max(1, c))
      : Array.from({ length: R }, () => 1);

  for (let m = 0; m < R; m++) {
    const rowKey = `row${m}`;
    if (outputManualByMaterialRow[rowKey]) continue;

    const qtyKg = Number(
      String(rawMaterials[m]?.qty ?? "")
        .replace(/,/g, "")
        .trim()
    );
    if (!Number.isFinite(qtyKg) || qtyKg <= 0) continue;

    const refKey = `embodiedCarbon_referenceQty_${carbonCode}_row${m}`;
    const fnKey = `embodiedCarbon_functionalUnit_${carbonCode}_row${m}`;
    const refUnitKey = `embodiedCarbon_referenceQty_unit_${carbonCode}_row${m}`;
    const fnUnitKey = `embodiedCarbon_functionalUnit_unit_${carbonCode}_row${m}`;

    const nV = vCounts[m] ?? 1;

    if (nV <= 1) {
      const vStart = sumPerMaterialCounts(vCounts, m);
      const cf = Number(
        currentValues[`characterizationFactor_${carbonCode}_row${m}`] ?? ""
      );
      const capacityT = parseVehicleCapacityTonnes(
        currentValues[`vehicleCapacity_${A2_VEHICLE_CODE}_row${vStart}`] ?? ""
      );
      let totalDist = parseDistanceKm(
        currentValues[
          `totalDistanceTravelled_${A2_VEHICLE_CODE}_row${vStart}`
        ] ?? ""
      );
      if (totalDist == null) {
        totalDist = parseDistanceKm(
          currentValues[`distanceBetweenSites_${A2_TRANSPORT_CODE}_row${m}`] ??
            ""
        );
      }

      if (
        !Number.isFinite(cf) ||
        cf <= 0 ||
        capacityT == null ||
        totalDist == null
      ) {
        continue;
      }

      const refKg = cf * capacityT * totalDist;
      if (!Number.isFinite(refKg)) continue;

      updates[refKey] = formatEmbodiedCarbonReferenceQty(refKg);
      updates[fnKey] = formatEmbodiedCarbonFunctionalUnit(refKg / qtyKg);
      if (kgCo2eUnitId) {
        if (currentValues[refUnitKey] !== kgCo2eUnitId) {
          updates[refUnitKey] = kgCo2eUnitId;
        }
        if (fnUnitToken && currentValues[fnUnitKey] !== fnUnitToken) {
          updates[fnUnitKey] = fnUnitToken;
        }
      }
      continue;
    }

    let refKg = 0;
    const vStart = sumPerMaterialCounts(vCounts, m);
    for (let j = 0; j < nV; j++) {
      const flat = vStart + j;
      const dist = parseDistanceKm(
        currentValues[
          `totalDistanceTravelled_${A2_VEHICLE_CODE}_row${flat}`
        ] ?? ""
      );
      const capacityT = parseVehicleCapacityTonnes(
        currentValues[`vehicleCapacity_${A2_VEHICLE_CODE}_row${flat}`] ?? ""
      );
      const cat =
        currentValues[`vehicleCategory_${A2_VEHICLE_CODE}_row${flat}`] ?? "";
      const pub = findVehicleCategoryEmissionFactor(
        cat,
        vehicleCategoryEmissionFactors
      );
      if (!pub) continue;
      const cf = parseLeadingNumberFromDisplay(pub.emissionFactor);
      if (cf == null || !Number.isFinite(cf) || cf <= 0) continue;
      if (dist == null || capacityT == null) continue;

      refKg += cf * capacityT * dist;
    }

    if (!Number.isFinite(refKg) || refKg <= 0) continue;

    updates[refKey] = formatEmbodiedCarbonReferenceQty(refKg);
    updates[fnKey] = formatEmbodiedCarbonFunctionalUnit(refKg / qtyKg);
    if (kgCo2eUnitId) {
      if (currentValues[refUnitKey] !== kgCo2eUnitId) {
        updates[refUnitKey] = kgCo2eUnitId;
      }
      if (fnUnitToken && currentValues[fnUnitKey] !== fnUnitToken) {
        updates[fnUnitKey] = fnUnitToken;
      }
    }
  }

  return updates;
}

function hasStoredUnitValue(v: unknown): boolean {
  return String(v ?? "").trim() !== "";
}

function resolveCfUnitFromHint(
  hint: string,
  carbon: FormSubSection | undefined,
  unitIdByToken: Record<string, number>,
  optionRows?: { value: string; label: string }[]
): string {
  const token = String(hint ?? "").trim();
  if (!token) return "";

  if (carbon) {
    const fromField = resolveUnitValueForCarbonField(
      carbon,
      "characterizationFactor_unit",
      token,
      unitIdByToken
    );
    if (fromField) return fromField;
  }

  if (optionRows?.length) {
    const target = normalizeUnitToken(token);
    const lower = token.toLowerCase();
    for (const o of optionRows) {
      const lblNorm = normalizeUnitToken(o.label);
      const lblLower = o.label.toLowerCase();
      if (
        lblNorm === target ||
        lblLower === lower ||
        lblLower.includes(lower) ||
        lower.includes(lblLower)
      ) {
        return o.value;
      }
    }
  }

  return "";
}

/**
 * Fill missing unit dropdown values before A2 validation/save.
 * CMS includes legacy hidden "Unit" text fields; UI stores units as CMS ids on `*_unit` keys.
 */
export function hydrateA2UnitFieldsBeforeValidation(params: {
  values: Record<string, string>;
  formData: { formSubSections?: FormSubSection[] } | null;
  rawMaterialsLength: number;
  vehiclesPerRawMaterial: number[];
  vehicleCategoryEmissionFactors: VehicleCategoryEmissionFactorPublic[];
  unitIdByToken: Record<string, number>;
  cfUnitHintByMaterial: Record<number, string>;
  cfUnitSelectOptions?: { value: string; label: string }[];
  kgCo2eUnitId?: string;
  functionalCarbonUnitValue?: string;
  mjUnitId?: string | null;
  fuelFlatRowCount: number;
  carbonCode?: string;
  fuelCode?: string;
}): Record<string, string> {
  const {
    values,
    formData,
    rawMaterialsLength,
    vehiclesPerRawMaterial,
    vehicleCategoryEmissionFactors,
    unitIdByToken,
    cfUnitHintByMaterial,
    cfUnitSelectOptions = [],
    kgCo2eUnitId = "",
    functionalCarbonUnitValue = "",
    mjUnitId = null,
    fuelFlatRowCount,
    carbonCode = "A2_EMBODIED_CARBON",
    fuelCode = "A2_FUEL_TRANSPORT",
  } = params;

  const next = { ...values };
  const carbon = formData?.formSubSections?.find((s) => s.code === carbonCode);

  const vCounts =
    vehiclesPerRawMaterial.length >= rawMaterialsLength
      ? vehiclesPerRawMaterial.map((c) => Math.max(1, c))
      : Array.from({ length: Math.max(0, rawMaterialsLength) }, () => 1);

  for (let m = 0; m < rawMaterialsLength; m++) {
    const cfKey = `characterizationFactor_${carbonCode}_row${m}`;
    const cfUnitKey = `characterizationFactor_unit_${carbonCode}_row${m}`;
    if (!hasStoredUnitValue(next[cfKey])) continue;
    if (hasStoredUnitValue(next[cfUnitKey])) continue;

    let hint = String(cfUnitHintByMaterial[m] ?? "").trim();
    if (!hint && (vCounts[m] ?? 1) <= 1 && vehicleCategoryEmissionFactors.length) {
      const vStart = sumPerMaterialCounts(vCounts, m);
      const cat =
        next[`vehicleCategory_${A2_VEHICLE_CODE}_row${vStart}`] ?? "";
      const pub = findVehicleCategoryEmissionFactor(
        cat,
        vehicleCategoryEmissionFactors
      );
      hint = String(pub?.unit ?? "").trim();
    }

    const resolved = resolveCfUnitFromHint(
      hint,
      carbon,
      unitIdByToken,
      cfUnitSelectOptions
    );
    if (resolved) next[cfUnitKey] = resolved;
  }

  const fnUnitToken =
    String(functionalCarbonUnitValue ?? "").trim() || kgCo2eUnitId;

  if (kgCo2eUnitId || fnUnitToken) {
    for (let m = 0; m < rawMaterialsLength; m++) {
      const refQKey = `embodiedCarbon_referenceQty_${carbonCode}_row${m}`;
      const refUKey = `embodiedCarbon_referenceQty_unit_${carbonCode}_row${m}`;
      if (hasStoredUnitValue(next[refQKey]) && !hasStoredUnitValue(next[refUKey])) {
        next[refUKey] = kgCo2eUnitId;
      }

      const fnQKey = `embodiedCarbon_functionalUnit_${carbonCode}_row${m}`;
      const fnUKey = `embodiedCarbon_functionalUnit_unit_${carbonCode}_row${m}`;
      if (hasStoredUnitValue(next[fnQKey]) && !hasStoredUnitValue(next[fnUKey])) {
        next[fnUKey] = fnUnitToken;
      }
    }
  }

  const energyUnit = (mjUnitId && String(mjUnitId).trim()) || "MJ";
  for (let i = 0; i < fuelFlatRowCount; i++) {
    const eKey = `embodiedEnergy_${fuelCode}_row${i}`;
    const uKey = `embodiedEnergy_unit_${fuelCode}_row${i}`;
    if (hasStoredUnitValue(next[eKey]) && !hasStoredUnitValue(next[uKey])) {
      next[uKey] = energyUnit;
    }
  }

  return next;
}
